"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Target, TrendingUp, Compass, ArrowRight, ShieldCheck, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function SeoIntro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const featureCards = [{
    icon: Globe,
    tag: "Cross-Market SEO",
    title: "SEO Across Diverse Models & Markets",
    description: "From niche UK service providers and national B2B firms to complex international ecommerce stores, I adapt search strategy to your exact operational model and competitive landscape.",
    bullet: "UK-based consultant with proven international SEO capability."
  }, {
    icon: TrendingUp,
    tag: "Sustainable Value",
    title: "Data-Led Strategies for Organic Revenue",
    description: "My SEO consultancy focuses on sustainable search performance rather than quick-win gimmicks. I build durable search authority that survives search algorithm updates, drives high-intent organic visitors, and delivers measurable commercial returns.",
    bullet: "Transparent reporting with honest measurement and clear ROI."
  }];
  return <section id="seoconsultantukintro" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 text-foreground">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Editorial Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center">
          <motion.div variants={itemVariants} className="flex justify-center mb-5">
            <Badge variant="outline" className="gap-1.5 border-border bg-secondary/50 px-3.5 py-1 text-xs font-medium text-foreground uppercase tracking-wide rounded-full">
              <Compass className="size-3.5 text-primary" />
              <span>Independent UK SEO Consultancy</span>
            </Badge>
          </motion.div>

          <motion.h1 variants={itemVariants} className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-[1.18]">
            SEO Consultant
          </motion.h1>

          <motion.p variants={itemVariants} className="mt-3 font-serif text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight text-primary">
            Tailored SEO Strategies for Sustainable Growth
          </motion.p>

          <motion.div variants={itemVariants} className="mt-8 space-y-5 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            <p>
              I am Yassine Essaadi, an independent professional SEO consultant and recognised SEO expert helping UK, national, and international businesses build genuine, sustainable organic search visibility. As an experienced SEO strategy consultant, I recognise that generic agency templates no longer move the needle in an era where search engines value authentic authority and clear user intent.
            </p>
            <p>
              Through my tailored SEO consultancy services as a <Link to="/FreelanceSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">freelance SEO consultant</Link>, I deliver search strategies that combine rigorous technical SEO, semantic content structuring, and honest measurement. By working directly with me, your business gains focused expertise engineered specifically around your commercial goals.
            </p>
          </motion.div>
        </motion.div>

        {/* Two-Column Structured Strategy Cards */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mt-14 md:mt-18 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {featureCards.map((card, i) => {
          const Icon = card.icon;
          return <motion.div key={card.title} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md relative overflow-hidden group">
                  <div className="absolute top-0 left-0 h-1 w-full bg-linear-to-r from-primary to-accent opacity-90" />
                  
                  <CardHeader className="space-y-3 pb-2 pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-secondary text-primary">
                        <Icon className="size-5" />
                      </div>
                      <span className="rounded-full bg-secondary/80 px-2.5 py-0.5 text-xs font-medium text-foreground">
                        {card.tag}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl md:text-2xl font-bold tracking-tight text-card-foreground">
                      {card.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2 font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                    <p>{card.description}</p>
                    
                    <div className="flex items-start gap-2 pt-2 text-xs md:text-sm font-medium text-foreground">
                      <ShieldCheck className="size-4 shrink-0 text-primary mt-0.5" />
                      <span>{card.bullet}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action Bar */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="h-12 px-8 text-base w-full sm:w-auto font-medium">
            <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
              <span>Hire Me Directly</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
            <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
          </Button>
        </motion.div>

        {/* Subtle Trust Bar */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mt-12 pt-8 border-t border-border flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-sm text-muted-foreground font-sans">
          <span className="flex items-center gap-1.5">
            <UserCheck className="size-4 text-primary" /> Data-Led Search Strategy
          </span>
          <span className="flex items-center gap-1.5">
            <Target className="size-4 text-primary" /> Tailored Search Roadmap
          </span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="size-4 text-primary" /> Transparent Reporting
          </span>
        </motion.div>
      </div>
    </section>;
}